import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from 'components/elements/Loader';
import { FlexCenter } from 'resources/styles/display';
import useUsers from 'hooks/useUsers';
import useRequest from 'hooks/useRequest';
import api from 'api';
import Modal from 'components/elements/BasicModal';
import useFeedback from 'hooks/useFeedback';
import { MAX_PAGE_SIZE } from 'utils/constants';
import { BurnCenterType } from 'types/burnCenter';
import {
  Form, Alert, Stack, Container,
} from 'react-bootstrap';
import SVButton from 'components/elements/SVButton';
import colors from 'resources/styles/colors';
import useUser from 'hooks/useUser';
import { Roles } from 'utils/enums';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { formatUTCDate } from 'utils/dateTime';
import { SectionTitle, SectionWrapperBox } from 'components/layout/SectionContainer';
import UserForm from './UserForm';
import schema from './schema';
import { UserFormValues } from './types';
import { toClientField } from './helper';

const mapApiToInitial = (u: any): Partial<UserFormValues> => ({
  name: u.name ?? '',
  nickname: u.nickname ?? '',
  phoneNumber: u.phoneNumber ?? '',
  title: u.title ?? '',
  credentials: u.credentials ?? '',
  email: u.email ?? '',
  burnCenterId: u.burnCenterId ?? null,
  active: !!u.active,
  canAccessVerification: !!u.canAccessVerification,
  canAccessBedEntry: !!u.canAccessBedEntry,
  canUpdateTrainingContent: !!u.canUpdateTrainingContent,
  role: (u.role ?? null) as any,
  reportingRole: (u.reportingRole ?? null) as any,
  clinicalStudioRole: (u.clinicalStudioRole ?? null) as any,
});

const updateBtnLabelIdle = 'Update';
const updateBtnLabelBusy = 'Updating user...';

const UserEdit: React.FC = () => {
  const { id, userId } = useParams<{ id: string; userId: string }>();
  const navigate = useNavigate();
  const {
    isAdmin, isBdataAmin, isAbaAdmin, appUser, isBcAdmin,
  } = useUser();
  const {
    user, loading, refetch, error,
  } = useUsers(userId ?? id!);
  const [confirmResetMfa, setConfirmResetMfa] = useState(false);
  const [allowEdit, setAllowEdit] = useState(false);

  const [mfaConfirmText, setMfaConfirmText] = useState('');
  const mfaCanConfirm = mfaConfirmText.trim().toLowerCase() === 'reset';

  const [busy, setBusy] = useState<null | 'reset' | 'disable'>(null);
  const { setError, setSuccess } = useFeedback?.() ?? {
    setError: () => {},
    setSuccess: () => {},
  };

  const fixedBurnCenterId = isBcAdmin
    ? (appUser?.burnCenterId ?? null)
    : undefined;
  const disallowedRoles = isBcAdmin ? [Roles.bdataAdmin, Roles.abaAdmin] : [];
  const {
    doRequest: getBurnCenters,
    data: bcResponse,
    isLoading: centersLoading,
    error: centersError,
  } = useRequest(api.getBurnCenters);

  const {
    doRequest: getUserMfa,
    data: mfaResponse,
  } = useRequest(api.getUserMfa);

  useEffect(() => {
    if (!isBcAdmin) {
      getBurnCenters({ PageSize: MAX_PAGE_SIZE });
    }
  }, [getBurnCenters, isBcAdmin]);

  useEffect(() => {
    if (!isBcAdmin) {
      getBurnCenters({ PageSize: MAX_PAGE_SIZE });
    }
  }, [getBurnCenters, isBcAdmin]);
  const burnCenters: BurnCenterType[] = isBcAdmin
    ? []
    : ((bcResponse as any)?.items ?? (bcResponse as any)?.data ?? []);

  const form = useForm<UserFormValues>({
    defaultValues: user ? mapApiToInitial(user) : {},
    resolver: yupResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if ((isAdmin || isBdataAmin || isBcAdmin) && !isAbaAdmin) getUserMfa(userId ?? id!);
  }, [getUserMfa, userId, id, isAdmin, isBdataAmin, isBcAdmin, isAbaAdmin]);

  useEffect(() => {
    if (user) {
      form.reset(mapApiToInitial(user));
    }
  }, [user, form]);

  if ((loading || !user) && !error) {
    return (
      <FlexCenter style={{ width: '100%', height: 300 }}>
        <Loader />
      </FlexCenter>
    );
  }

  if (error) {
    return (
      <FlexCenter style={{ width: '100%', height: 300 }}>
        <Alert variant="danger">Error Loading User</Alert>
      </FlexCenter>
    );
  }

  const resetMfaAction = () => {
    setConfirmResetMfa(true);
    setMfaConfirmText('');
  };
  const doResetUserMfa = async () => {
    try {
      setBusy('reset');
      await api.resetUserMfa(userId ?? id);
      await refetch();
      await getUserMfa(userId ?? id);
      setSuccess?.('MFA has been reset for this user.');
      setConfirmResetMfa(false);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError?.(detail || 'Failed to reset MFA.');
    } finally {
      setBusy(null);
    }
  };

  const doDisableUser = async () => {
    try {
      setBusy('disable');
      const isDisabled = !!user?.disabledAt;
      if (isDisabled) {
        await api.enableUser(userId ?? id);
        setSuccess?.('User has been reactivated.');
      } else {
        await api.disableUser(userId ?? id);
        setSuccess?.('User has been disabled.');
      }
      setBusy(null);
      await refetch();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError?.(detail || 'Failed to disable user.');
      setBusy(null);
    }
  };

  const handleUpdate = async (payload: UserFormValues) => {
    try {
      await api.updateUser(userId ?? id, payload);
      await refetch();
      if (userId && id) {
        navigate(`/burn-centers/${id}/detail?tab=Users`);
      } else {
        navigate('..');
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const fieldErrors = err?.response?.data?.errors;

      if (fieldErrors && typeof fieldErrors === 'object') {
        Object.entries(fieldErrors).forEach(([k, v]) => {
          const msg = Array.isArray(v) ? v.join(' ') : String(v);
          form.setError(toClientField(k), { type: 'server', message: msg });
        });
      }

      setError?.(detail || 'Failed to update user.');
    }
  };

  const cancelEditing = () => {
    form.reset(mapApiToInitial(user));
    setAllowEdit(false);
  };

  return (
    <Container className="site-content">
      <SectionWrapperBox>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <SectionTitle>{user?.name ?? 'User Details'}</SectionTitle>
          {(isAdmin || isBdataAmin || isAbaAdmin || isBcAdmin) && (
          <div className="d-flex flex-column align-items-end">
            {user?.disabledAt && (
            <div className="mb-2">
              <small>
                Account disabled on:
                {' '}
                {formatUTCDate(user.disabledAt)}
              </small>
            </div>
            )}
            {((isAdmin || isBdataAmin || isBcAdmin) && !isAbaAdmin)
            && (!mfaResponse || mfaResponse.length === 0) && (
            <div className="mb-2">
              <small>
                User does not have enrolled MFA device
              </small>
            </div>
            )}
            <Stack direction="horizontal" gap={3}>
              {!allowEdit && !isAbaAdmin && (isAdmin || isBdataAmin || isBcAdmin) && (
              <SVButton
                style={{ color: colors.grayDarkVariant }}
                variant="warning"
                type="button"
                onClick={() => setAllowEdit(true)}
                disabled={busy !== null}
              >
                Edit
              </SVButton>
              )}
              {allowEdit && (
              <>
                <SVButton
                  variant="cancel"
                  type="button"
                  onClick={cancelEditing}
                  disabled={busy !== null}
                >
                  Cancel
                </SVButton>
                <SVButton
                  variant="secondary"
                  type="button"
                  onClick={form.handleSubmit(handleUpdate)}
                  disabled={busy !== null || form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting
                    ? updateBtnLabelBusy
                    : updateBtnLabelIdle}
                </SVButton>
              </>
              )}
              {(isAdmin || isBdataAmin || isBcAdmin)
                && (user?.disabledAt && !isAbaAdmin) ? (
                  <SVButton
                    style={{ color: colors.grayDarkVariant }}
                    variant="warning"
                    type="button"
                    onClick={() => doDisableUser()}
                    disabled={busy !== null}
                  >
                    Reactivate Account
                  </SVButton>
                ) : null}

              {(isAdmin || isBdataAmin || isAbaAdmin || isBcAdmin)
                && (!user?.disabledAt) ? (
                  <SVButton
                    style={{ color: colors.grayDarkVariant }}
                    variant="warning"
                    type="button"
                    onClick={() => doDisableUser()}
                    disabled={busy !== null}
                  >
                    Disable
                  </SVButton>
                ) : null}

              {(isAdmin || isBdataAmin || isBcAdmin) && !isAbaAdmin
              && (mfaResponse && mfaResponse.length > 0) && (
                <SVButton
                  variant="danger"
                  type="button"
                  onClick={() => resetMfaAction()}
                  disabled={busy !== null}
                >
                  Reset MFA
                </SVButton>
              )}
            </Stack>
          </div>
          )}
        </div>
        <UserForm
          form={form}
          mode="edit"
          initialValues={mapApiToInitial(user)}
          onSubmit={handleUpdate}
          burnCenters={burnCenters}
          centersLoading={centersLoading}
          centersError={centersError}
          isAdmin={isBdataAmin || isBcAdmin}
          fixedBurnCenterId={fixedBurnCenterId}
          disallowedRoles={disallowedRoles}
          allowEdit={allowEdit}
          lockDisallowedEditRole
          cancelHref=".."
          onCancel={() => setAllowEdit(false)}
        />
        {confirmResetMfa && (
        <Modal
          isOpen
          title="Are you sure you would like to reset your user's MFA?"
          body={(
            <div>
              <p>
                Please understand that resetting this user&apos;s MFA will
                result in the need to reconfigure their Authenticator app.
              </p>

              <div>
                <p>
                  <b>Before continuing, please confirm the following:</b>
                </p>
                <ul>
                  <li>
                    The user no longer has access to their Authenticator app.
                  </li>
                  <li>The user has no access to their one-time password.</li>
                  <li>The user has not registered their MFA in another app.</li>
                </ul>
              </div>

              <div>
                <p>
                  If you understand this and can confirm please type
                  {' '}
                  <code>reset</code>
                  {' '}
                  to continue.
                </p>

                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!busy && mfaCanConfirm) {
                      doResetUserMfa();
                    }
                  }}
                >
                  <Form.Control
                    type="text"
                    value={mfaConfirmText}
                    onChange={(e) => setMfaConfirmText(e.target.value)}
                    autoFocus
                    autoComplete="off"
                    name="mfa_confirmation"
                    id="mfa-confirmation-input"
                    required
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (!busy && mfaCanConfirm) doResetUserMfa();
                      }
                    }}
                  />
                </Form>
              </div>
            </div>
          )}
          onConfirm={doResetUserMfa}
          onClose={() => (busy ? null : setConfirmResetMfa(false))}
          onConfirmTxt={busy === 'reset' ? 'Resetting…' : 'Yes, reset MFA'}
          disableConfirm={busy === 'reset' || !mfaCanConfirm}
        />
        )}
      </SectionWrapperBox>
    </Container>
  );
};

export default UserEdit;
